import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { contactInfo, submitContactForm } from '../utils/mockData';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { AttachmentPicker } from '../components/AttachmentPicker';

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const uploading = files.some((f) => f.status === 'uploading');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const attachment_ids = files.filter((f) => f.status === 'done').map((f) => f.id);
      const result = await submitContactForm({ ...formData, language, attachment_ids });
      if (result.success) {
        toast.success(t('contact.successMsg'));
        setFormData({ name: '', email: '', phone: '', message: '' });
        setFiles([]);
      }
    } catch (error) {
      // Show detailed error from backend if available
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Chyba: ${detail}` : t('contact.errorMsg'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white dark:from-black dark:via-gray-900 dark:to-black transition-colors duration-200 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">{t('contact.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">{t('contact.subtitle')}</p>
          <div className="w-24 h-1 bg-green-600 dark:bg-green-500 mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
                {t('contact.infoTitle')}
              </h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600/10 dark:bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-semibold mb-1">{t('contact.email')}</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600/10 dark:bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-semibold mb-1">{t('contact.phone')}</h3>
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600/10 dark:bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-semibold mb-1">{t('contact.address')}</h3>
                    <p className="text-gray-600 dark:text-gray-400" data-testid="contact-address">
                      {contactInfo.address.street}, {contactInfo.address.zip} {contactInfo.address.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Info + Map */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-lg">
              <h3 className="text-gray-900 dark:text-white font-bold text-xl mb-4">Fabrikov</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('contact.companyDesc')}
              </p>

              <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1500!2d15.0518097!3d50.9170286!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x470936a765c5c5a7%3A0x622f955e26133462!2sFabrikov%20s.r.o.!5e0!3m2!1scs!2scz!4v1760000000000"
                  data-testid="contact-map"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Fabrikov Map"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 shadow-xl">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('contact.formTitle')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-gray-900 dark:text-white font-medium mb-2">
                  {t('contact.labelName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-input-name"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors duration-200"
                  placeholder={t('contact.placeholderName')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-900 dark:text-white font-medium mb-2">
                  {t('contact.labelEmail')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-input-email"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors duration-200"
                  placeholder={t('contact.placeholderEmail')}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-900 dark:text-white font-medium mb-2">
                  {t('contact.labelPhone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="contact-input-phone"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors duration-200"
                  placeholder={t('contact.placeholderPhone')}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-900 dark:text-white font-medium mb-2">
                  {t('contact.labelMessage')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  data-testid="contact-input-message"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors duration-200 resize-none"
                  placeholder={t('contact.placeholderMessage')}
                />
              </div>

              <AttachmentPicker files={files} setFiles={setFiles} />

              <button
                type="submit"
                disabled={isSubmitting || uploading}
                data-testid="contact-submit-btn"
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 dark:bg-green-500 dark:hover:bg-green-600 text-white dark:text-black font-semibold px-6 py-4 rounded-lg transform hover:scale-105 disabled:transform-none transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg"
              >
                {isSubmitting ? (
                  <span>{t('contact.sending')}</span>
                ) : (
                  <>
                    <Send size={20} />
                    <span>{t('contact.send')}</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
